import { Button, Card, Col, DatePicker, Divider, Flex, Form, Row, Select, Typography } from "antd"
import { useIntl } from "react-intl"
import enLocale from "antd/es/date-picker/locale/en_GB";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import dayjs from "dayjs";
import Image from "next/image";
import "./OfferPassLeftSection.css";
import { ICmsAboutPass, ICmsMonthlyPassOffer, ICmsTravelPassOffer } from "../../Orchestrator/Types/Interface/ICMSOffers";
import { getRecoilStateValue, useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import stopManager from "../../Lib/Utils/StopManager";
import { useEffect, useMemo, useState } from "react";
import { _eventBus } from "../../core/events/EventBus";
import { IPassOffer, IPassOfferResponse } from "../api/getPassOffers";
import { findTicketOptionForProduct } from "../../Lib/Utils/ticketOptionMatcher";
import { IOfferPassType } from "../../Orchestrator/Types/Types/OffersResponse";
import { IBookingResponse, ICustomerContact, IPassengerSelection, IUserResponse } from "../../Orchestrator/Types/Index";
import { evtTree } from "../../Orchestrator/ConstantTree";
import { PASSENGER_WITH_BIRTHDATE_TYPES } from "../../config/global";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { showNotification } from "../../Services/notificationService";
import GetInvoice from "../../Components/Checkout/CheckoutPage/GetInvoice/GetInvoice";
import RedCaretIcon from "../../Components/Shared/RedCaretIcon";
import { CustomerContactForm } from "../../Components/Checkout/CheckoutPage/CustomerContact/CustomerContact";
import { checkoutTree } from "../../Orchestrator/ConstantTree/CheckoutPage/CheckoutTree";
import { enumOfferPassType } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ITicketOption } from "../../Orchestrator/Types/Interface/ITicketOption";
import { getArabicTranslatedMessage } from "../../Lib/Utils/i18n";
dayjs.extend(utc);
dayjs.extend(timezone);

export const identifyPassTypeFromCode = (code: string) => {
  // Regex to check if the code contains a number followed by 'x' or 'X'
  const regex = /\d+[xX]/

  if (regex.test(code)) {
    return enumOfferPassType.travelPass
  } else {
    return enumOfferPassType.monthlyPass
  }
}

export interface IPassForm {
  duration: number;
  numberOfDays: number;
  classTicketOption: string;
  passengerType: string;
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  birthDate?: dayjs.Dayjs;
}

const OfferPassLeftSection = () => {
  const intl = useIntl();

  const [selectedCmsPassOfferAtom] = useRecState<ICmsMonthlyPassOffer | ICmsTravelPassOffer | null>("selectedCmsPassOfferAtom");
  const [aboutPassDetailAtom] = useRecState<ICmsAboutPass | null>("cmsAboutPassDetailAtom");
  const [passOfferResponseAtom] = useRecState<IPassOfferResponse>("passOfferResponseAtom");
  const [offerPassTypeAtom] = useRecState<IOfferPassType | null>("offerPassTypeAtom");
  const [passengerTypesStateAtom] = useRecState<IPassengerSelection[]>("passengerTypesStateAtom");
  const [calendarDayInterval] = useRecState<number>("calendarDayIntervalAtom");
  const [bookingResponseAtom] = useRecState<IBookingResponse | null>("bookingResponseAtom");
  const [selectedPassOfferAtom] = useRecState<IPassOffer | null>("selectedPassOfferAtom");
  const [ticketOptionsAtom] = useRecState<ITicketOption[]>("ticketOptionsAtom");
  const [customerContact] = useRecState<ICustomerContact>("customerContactStateAtom");
  const [isAnonymous] = useRecState<boolean>("isAnonymousStateAtom");
  const [userData] = useRecState<IUserResponse>("checkoutUserDataAtom");

  const [passForm] = Form.useForm();

  const passFormValues = Form.useWatch([], passForm);

  useEffect(() => {
    if (passFormValues) {
      const allValuesAreValid = Object.values(passFormValues).every(value => value !== undefined && value !== null);
      if (allValuesAreValid) {
        setTimeout(() => {
          _eventBus.emit({
            eventName: evtTree.CMSOffers.OfferPass.formValuesChange,
            data: {
              formValues: passFormValues
            }
          })
        })
      }
    }
  }, [passFormValues])

  const fetchPassOffers = (partialForm: Object) => {
    let payload = {
      startDate: passFormValues.startDate || dayjs(),
      passengerType: passFormValues.passengerType || 'adults',
      ...partialForm
    }

    _eventBus.emit({
      eventName: evtTree.CMSOffers.OfferPass.getOffers,
      data: payload
    })
  }

  useEffect(() => {
    setTimeout(() => {
      const persistedPassFormValues = getRecoilStateValue<IPassForm>("passFormValuesAtom");

      if (persistedPassFormValues) {
        passForm.setFieldsValue({
          duration: persistedPassFormValues.duration,
          numberOfDays: persistedPassFormValues.numberOfDays,
          classTicketOption: persistedPassFormValues.classTicketOption,
          passengerType: persistedPassFormValues.passengerType,
          startDate: persistedPassFormValues.startDate ? dayjs(persistedPassFormValues.startDate) : undefined,
          endDate: persistedPassFormValues.endDate ? dayjs(persistedPassFormValues.endDate) : undefined,
          birthDate: persistedPassFormValues.birthDate ? dayjs(persistedPassFormValues.birthDate) : undefined,
        })
      } else {
        passForm.setFieldsValue({
          startDate: dayjs(new Date()),
          passengerType: 'adults'
        })
        setTimeout(() => {
          _eventBus.emit({
            eventName: evtTree.CMSOffers.OfferPass.getOffers,
            data: {
              startDate: dayjs(new Date()),
              passengerType: 'adults'
            }
          })
        })
      }
    }, 500)
  }, [])

  const aboutPass = useMemo(() => {
    return [{
      icon: '/assets/offers/info-circle.svg',
      title: intl.formatMessage({ id: offerPassTypeAtom === enumOfferPassType.monthlyPass ? "offer.whatIsMonthlyPass" : "offer.whatIsTravelPass" }),
      description: aboutPassDetailAtom?.what_is_pass || ""
    }, {
      icon: '/assets/offers/star.svg',
      title: intl.formatMessage({ id: "offer.benefits" }),
      description: aboutPassDetailAtom?.benefits || ""
    }, {
      icon: '/assets/offers/credit-card.svg',
      title: intl.formatMessage({ id: "offer.howToBuy" }),
      description: aboutPassDetailAtom?.how_to_buy || ""
    }, {
      icon: '/assets/offers/check-circle.svg',
      title: intl.formatMessage({ id: "offer.howToUse" }),
      description: aboutPassDetailAtom?.how_to_use || ""
    }]
  }, [aboutPassDetailAtom]);

  const selectedPassOffer = useMemo(() => {
    if (offerPassTypeAtom === enumOfferPassType.monthlyPass) {
      return passOfferResponseAtom?.offers.find(i => i.admissionOfferParts.some(i => i.indicatedConsumption.unit.objectType === "DayTravelAccountUnit" && i.indicatedConsumption.amount === passFormValues?.duration))
    } else if (offerPassTypeAtom === enumOfferPassType.travelPass) {
      return passOfferResponseAtom?.offers.find(i => i.admissionOfferParts.some(i => i.indicatedConsumption.unit.objectType === "TripTravelAccountUnit" && i.indicatedConsumption.amount === passFormValues?.numberOfDays))
    }
    return undefined
  }, [passOfferResponseAtom, passFormValues]);

  const availableOfferOptions = useMemo(() => {
    if (offerPassTypeAtom === enumOfferPassType.monthlyPass) {
      return passOfferResponseAtom?.offers.filter(i => i.admissionOfferParts.some(i => i.indicatedConsumption.unit.objectType === "DayTravelAccountUnit")) || []
    } else if (offerPassTypeAtom === enumOfferPassType.travelPass) {
      return passOfferResponseAtom?.offers.filter(i => i.admissionOfferParts.some(i => i.indicatedConsumption.unit.objectType === "TripTravelAccountUnit")) || []
    }
    return []
  }, [passOfferResponseAtom, offerPassTypeAtom]);

  const durationOptions = useMemo(() => {
    return availableOfferOptions.map(offer => ({
      label: offer.admissionOfferParts[0].indicatedConsumption.amount + " Days",
      value: offer.admissionOfferParts[0].indicatedConsumption.amount
    })).sort((a, b) => a.value - b.value) || []
  }, [availableOfferOptions]);

  const noOfTripsOptions = useMemo(() => {
    return availableOfferOptions.map(offer => ({
      label: offer.admissionOfferParts[0].indicatedConsumption.amount + " Trips",
      value: offer.admissionOfferParts[0].indicatedConsumption.amount
    })).sort((a, b) => a.value - b.value) || []
  }, [availableOfferOptions]);

  const classTicketOptions: ITicketOption[] = useMemo(() => {
    if (!selectedPassOffer) return [];

    // Pass 1: prefer the canonical meta record (lets the dropdown render
    // through the translation hook `ticketOption.<value>.label`). Matches
    // each product's `productTags` + `serviceClass.type` against the meta
    // records — resilient to new backend product codes (e.g. `"3DAYS"`)
    // that the old hardcoded code lists silently dropped.
    const fromMeta = new Map<string, ITicketOption>();
    for (const product of selectedPassOffer.products) {
      const matched = findTicketOptionForProduct(product, ticketOptionsAtom);
      if (matched) fromMeta.set(matched.value, matched);
    }
    if (fromMeta.size > 0) return Array.from(fromMeta.values());

    // Pass 2 (fallback): the meta's tier vocabulary doesn't always line
    // up with the backend's product-tag vocabulary — e.g. a travel-pass
    // product can carry `productTags: ["Standard Flex"]` while the meta
    // only knows about Saver / Value tiers, so the strict matcher
    // returns nothing and the dropdown goes empty. Synthesize a
    // dropdown option from the product's own tag + serviceClass so the
    // user can still see and pick what the backend is offering. Keyed
    // by `product.code` to dedupe identical products on the same offer.
    const synthetic = new Map<string, ITicketOption>();
    for (const product of selectedPassOffer.products) {
      const tag = (product.productTags || []).find(Boolean);
      const serviceClass = product.serviceClass?.type || "";
      if (!tag || !serviceClass || synthetic.has(product.code)) continue;
      synthetic.set(product.code, {
        value: product.code,
        label: tag,
        description: product.description || "",
        serviceClass,
      });
    }
    return Array.from(synthetic.values());
  }, [selectedPassOffer, ticketOptionsAtom]);

  useEffect(() => {
    if (passOfferResponseAtom) {
      if (offerPassTypeAtom === enumOfferPassType.monthlyPass) {
        passForm.setFieldValue("duration", durationOptions[0]?.value || null);
      } else if (offerPassTypeAtom === enumOfferPassType.travelPass) {
        passForm.setFieldValue("numberOfDays", noOfTripsOptions[0]?.value || null);
      }
    }
  }, [passOfferResponseAtom])

  // Prefill class ticket options after getting offers
  useEffect(() => {
    if (classTicketOptions.length > 0) {
      passForm.setFieldValue("classTicketOption", classTicketOptions[0]?.value || null)
    }
  }, [classTicketOptions])

  useEffect(() => {
    if (!selectedPassOffer && Array.isArray(passOfferResponseAtom?.offers)) {
      // No Offer found for current user
      showNotification(
        "error",
        intl.formatMessage({ id: "offer.noPassOfferFoundMessage" }),
        intl.formatMessage({ id: 'offer.noPassOfferFoundErrorDescription' })
      )
      passForm.setFieldsValue({
        duration: null,
        numberOfDays: null,
        classTicketOption: null
      });
    }
  }, [selectedPassOffer])

  const [isPassFormValid, setIsPassFormValid] = useState(false)

  useEffect(() => {
    passForm
      .validateFields({ validateOnly: true })
      .then(() => setIsPassFormValid(true))
      .catch((error) => {
        if (!error.outOfDate) {
          setIsPassFormValid(false)
        }
      });
  }, [passForm, passFormValues]);

  const handleConfirmPass = () => {
    _eventBus.emit({
      eventName: evtTree.CMSOffers.OfferPass.confirmPass,
      data: {
        selectedPassOffer: selectedPassOffer,
      }
    })
  }

  const handleModifyOffer = () => {
    _eventBus.emit({
      eventName: evtTree.CMSOffers.OfferPass.Modify.Click,
      data: {
        bookingId: bookingResponseAtom?.booking.id || ""
      }
    })
  }

  const handleCustomerContactChange = (
    field: keyof typeof customerContact,
    value: string
  ) => {
    _eventBus.emit({
      eventName: checkoutTree.CustomerContact.Change,
      data: {
        field: field,
        value: value,
        customerContact: customerContact,
      },
    });
  };

  useEffect(() => {
    setTimeout(() => {
      _eventBus.emit({
        eventName: checkoutTree.PassengerDetails.UpdatePassengerData,
        data: {
          userData: userData,
          passengers: [],
          customerContact: customerContact,
        },
      });

      setTimeout(() => {
        _eventBus.emit({
          eventName: evtTree.CMSOffers.OfferPass.detailPage.customerContact.Sync,
        })
      }, 1000);
    }, 1000);
  }, [userData]);

  const disablePassFormInputs = useMemo(() => selectedPassOfferAtom !== null, [selectedPassOfferAtom]);

  const showBirthDateInput = PASSENGER_WITH_BIRTHDATE_TYPES.includes(passFormValues?.passengerType);

  const currentPassengerType = useMemo(
    () => passengerTypesStateAtom.find((type) => type.id === passFormValues?.passengerType),
    [passengerTypesStateAtom, passFormValues?.passengerType]
  );

  const passengerMinAge = currentPassengerType?.minAge || 0;
  const passengerMaxAge = currentPassengerType?.maxAge || 0;

  return (
    <Flex vertical gap={40} className="mb-50 offer-pass-left-section">
      <Flex vertical gap={16} className="w-100 mt-20">
        <Typography className="fw-600 fs-24 color-primary ff-wallop">
          {intl.formatMessage({ id: "offer.origin&Destination" })}
        </Typography>

        <Card className="card-shadow border-none" classNames={{ body: "p-12 pl-14" }}>
          <div className="origin-destination-info">
            <div className="down-arrow">
              <Image
                src="/assets/booking/arrow-right.svg"
                alt="arrow"
                width={20}
                height={10}
              />
            </div>
            <div className="flex-auto text-left">
              <Typography className="fs-20 fw-700">
                {stopManager.getStopNameById(selectedCmsPassOfferAtom?.origin.code || "") || selectedCmsPassOfferAtom?.origin.name}
              </Typography>
              <Typography className="fs-20 fw-700">
                {stopManager.getStopNameById(selectedCmsPassOfferAtom?.destination.code || "") || selectedCmsPassOfferAtom?.destination.name}
              </Typography>
            </div>
          </div>
        </Card>
      </Flex>

      <Flex vertical gap={16} className="w-100">
        <Typography className="fw-600 fs-24 color-primary ff-wallop">
          {intl.formatMessage({ id: offerPassTypeAtom === enumOfferPassType.monthlyPass ? "offer.aboutMonthlyPass" : "offer.aboutTravelPass" })}
        </Typography>

        <Card className="card-shadow border-none radius-12" classNames={{ body: "p-20" }}>
          <Row gutter={[16, 16]}>
            {
              aboutPass.map((passInfo, index) => (
                <Col
                  key={`${passInfo.title}-${index}`}
                  xs={24}
                  sm={12}
                >
                  <Flex gap={12} align="flex-start" className="about-card h-100">
                    <div className="info-icon-wrapper">
                      <Image
                        src={passInfo.icon}
                        alt="info-icon"
                        width={24}
                        height={24}
                      />
                    </div>
                    <Flex vertical gap={10}>
                      <Typography className="fs-16 fw-700 lh-normal">
                        {passInfo.title}
                      </Typography>
                      <Typography className="fs-14 fw-400 color-757575">
                        {passInfo.description}
                      </Typography>
                    </Flex>
                  </Flex>
                </Col>
              ))
            }
          </Row>
        </Card>
      </Flex>

      <Flex vertical gap={16} className="w-100">
        <Typography className="fw-600 fs-24 color-primary ff-wallop">
          {
            offerPassTypeAtom === enumOfferPassType.monthlyPass ?
              intl.formatMessage({ id: "offer.configureMonthlyPass" })
              :
              intl.formatMessage({ id: "offer.configureTravelPass" })
          }
        </Typography>

        <Card className="card-shadow border-none radius-12" classNames={{ body: "p-0" }}>
          <Flex align="center" justify="space-between">
            <Typography className="fw-700 fs-16 p-20">
              {intl.formatMessage({ id: "offer.fillPassDetails" })}
            </Typography>
            {
              disablePassFormInputs &&
              <Button type="link" onClick={handleModifyOffer} className="color-primary fw-700 fs-16 mr-8">
                {intl.formatMessage({ id: "offer.change" })}
              </Button>
            }
          </Flex>

          <Divider className="mt-0 mb-0" />

          <Form
            form={passForm}
            name="pass_form"
            layout="vertical"
          >
            <Row className="p-20" gutter={[10, 10]}>
              {
                offerPassTypeAtom === enumOfferPassType.monthlyPass ?
                  <Col span={10}>
                    <Form.Item name="duration" label={intl.formatMessage({ id: "offer.durationLabel" })}
                      rules={[{
                        required: enumOfferPassType.monthlyPass === offerPassTypeAtom,
                        message: intl.formatMessage({ id: "offer.durationRequired" })
                      }]}
                      required={false}
                    >
                      <Select
                        className="pass-form-select"
                        suffixIcon={<RedCaretIcon />}
                        placeholder={intl.formatMessage({ id: "offer.durationPlaceholder" })}
                        disabled={disablePassFormInputs}
                      >
                        {durationOptions?.map(option => (
                          <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col> :
                  <Col span={10}>
                    <Form.Item
                      name="numberOfDays"
                      label={intl.formatMessage({ id: "offer.numberOfTripsLabel" })}
                      rules={[{
                        required: enumOfferPassType.travelPass === offerPassTypeAtom,
                        message: intl.formatMessage({ id: "offer.numberOfTripsRequired" })
                      }]}
                      required={false}
                    >
                      <Select
                        className="pass-form-select"
                        suffixIcon={<RedCaretIcon />}
                        placeholder={intl.formatMessage({ id: "offer.numberOfTripsPlaceholder" })}
                        disabled={disablePassFormInputs}
                      >
                        {noOfTripsOptions?.map(option => (
                          <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
              }

              <Col span={7}>
                <Form.Item
                  name="startDate"
                  label={intl.formatMessage({ id: "offer.startDate" })}
                  rules={[{
                    required: true,
                    message: intl.formatMessage({ id: "offer.startDateRequiredMessage" }),
                  }]}
                  required={false}
                >
                  <DatePicker
                    locale={intl.locale === "en" ? enLocale : arLocale}
                    inputReadOnly={true}
                    className="select-date"
                    format="DD/MM/YYYY"
                    disabled={disablePassFormInputs}
                    disabledDate={(current) =>
                      current &&
                      (current < dayjs().startOf("day") ||
                        current > dayjs().add(calendarDayInterval | 1, "days"))
                    }
                    onChange={(date) => fetchPassOffers({ startDate: date })}
                    suffixIcon={
                      <Image
                        src="/assets/search_icons/calendar.svg"
                        width={24}
                        height={24}
                        alt="add_calendar"
                      />
                    }
                    placeholder={intl.formatMessage({ id: "dateOfBirthPlaceholder" })}
                    allowClear={false}
                    classNames={{ popup: { root: "personal-info-date change-date-booking" } }}
                  />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label={intl.formatMessage({ id: "offer.endDate" })}>
                  <DatePicker
                    disabled
                    locale={intl.locale === "en" ? enLocale : arLocale}
                    inputReadOnly={true}
                    className="select-date"
                    format="DD/MM/YYYY"
                    value={dayjs(selectedPassOffer?.admissionOfferParts[0]?.validUntil)}
                    onChange={(date) => passForm.setFieldValue("endDate", date)}
                    suffixIcon={
                      <Image
                        src="/assets/search_icons/calendar.svg"
                        width={24}
                        height={24}
                        alt="add_calendar"
                      />
                    }
                    placeholder={intl.formatMessage({ id: "dateOfBirthPlaceholder" })}
                    allowClear={false}
                    classNames={{ popup: { root: "personal-info-date change-date-booking" } }}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="classTicketOption" label={intl.formatMessage({ id: "offer.classAndTicketOptionsLabel" })}
                  rules={[{
                    required: true,
                    message: intl.formatMessage({ id: "offer.classTicketOptionRequired" })
                  }]}
                  required={false}
                >
                  <Select
                    className="pass-form-select"
                    suffixIcon={<RedCaretIcon />}
                    placeholder={intl.formatMessage({ id: "offer.classAndTicketOptionPlaceholder" })}
                    disabled={disablePassFormInputs}
                  >
                    {
                      classTicketOptions.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          <Typography className={option.serviceClass === "PREMIUM" ? "premium-color" : "standard-color"}>
                            {getArabicTranslatedMessage(`ticketOption.${option.value}.label`, option.label || "")}
                          </Typography>
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>


              <Col span={showBirthDateInput ? 12 : 24}>
                <Form.Item
                  name="passengerType" label={intl.formatMessage({ id: "offer.passengerTypeLabel" })}
                >
                  <Select
                    className="pass-form-select"
                    suffixIcon={<RedCaretIcon />}
                    placeholder={intl.formatMessage({ id: "offer.passengerTypePlaceholder" })}
                    onChange={(value) => fetchPassOffers({ passengerType: value })}
                    disabled={disablePassFormInputs}
                  >
                    {
                      passengerTypesStateAtom.map(option => (
                        <Select.Option key={option.id} value={option.id}>{option.label}</Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
              {
                !!showBirthDateInput &&
                <Col span={showBirthDateInput ? 12 : 24}>
                  <Form.Item
                    name="birthDate"
                    label={intl.formatMessage({ id: "offer.birthDateLabel" })}
                    rules={[{
                      required: true,
                      message: "Required"
                    }]}
                    required={false}
                  >
                    <DatePicker
                      locale={intl.locale === "en" ? enLocale : arLocale}
                      inputReadOnly={true}
                      className="select-date"
                      format="DD/MM/YYYY"
                      defaultPickerValue={dayjs().subtract(passengerMinAge, "years")}
                      disabledDate={(current) =>
                        current &&
                        (current >
                          dayjs().subtract(passengerMinAge, "year").endOf("day") ||
                          current <
                          dayjs().subtract(passengerMaxAge, "year").startOf("day"))
                      }
                      disabled={disablePassFormInputs}
                      onChange={(date) => fetchPassOffers({ startDate: date })}
                      suffixIcon={
                        <Image
                          src="/assets/search_icons/calendar.svg"
                          width={24}
                          height={24}
                          alt="add_calendar"
                        />
                      }
                      placeholder={intl.formatMessage({ id: "dateOfBirthPlaceholder" })}
                      allowClear={false}
                      classNames={{ popup: { root: "personal-info-date change-date-booking" } }}
                    />
                  </Form.Item>
                </Col>
              }

              <Col span={24} className="mt-10 w-100">
                <div className="w-100 align-items-end">
                  <Button className="btn h-50 ml-auto radius-12 pl-24 pr-24 border-none" size="large" type="primary"
                    disabled={!isPassFormValid || disablePassFormInputs}
                    onClick={handleConfirmPass}
                  >
                    {intl.formatMessage({ id: "offer.confirmBtn" })}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      </Flex>


      {
        !isAnonymous &&
        <>
          <Flex vertical gap={16} className="w-100">
            <Typography className="fw-600 fs-24 color-primary ff-wallop">
              {intl.formatMessage({ id: "offer.passengerDetailsTitle" })}
            </Typography>

            <Card className="card-shadow border-none radius-12" classNames={{ body: "p-0" }}>
              <Typography className="fw-700 fs-16 p-20">
                {intl.formatMessage({ id: "offer.fillPassengerDetails" })}
              </Typography>

              <Divider className="mt-0 mb-0" />

              <div className="p-20">
                <CustomerContactForm
                  customerContact={customerContact}
                  handleCustomerContactChange={handleCustomerContactChange}
                />
              </div>
            </Card>
          </Flex>

          <GetInvoice />
        </>
      }

    </Flex >
  )
}

export default OfferPassLeftSection;
